import React, {useState} from 'react';
import {Form, Button, Steps, message} from 'antd';
import PersonalContactView from "./utils/personal-contact.view";
import SocialProfileView from "./utils/social-profile.view";
import AddressView from "./utils/address.view";
import '../../../../styles/component/employee/create/contact-details.scss'
import {IContactDetailFormModel} from "./types/contact-detail.types";

const {Step} = Steps;

interface IContactSteps {
    title: string;
    fields: string[][];
    content: React.ReactNode;
}

interface IContactDetailViewProps {
    loading: boolean;
    viewModel?: IContactDetailFormModel;
    onSubmit: (data: IContactDetailFormModel) => Promise<void>;
    goToNextTab: () => void;
}

const ContactDetailView = (props: IContactDetailViewProps) => {
    const [form] = Form.useForm();
    const [current, setCurrent] = useState(0);

    const steps: IContactSteps[] = [
        {
            title: 'Personal Contact',
            fields: [
                ['personal', 'mobileNumber'],
                ['personal', 'alternateMobileNumber'],
                ['personal', 'alternateEmailId']
            ],
            content: <PersonalContactView/>,
        },
        {
            title: 'Address',
            fields: [
                ['address', 'present', 'line1'],
                ['address', 'present', 'line2'],
                ['address', 'present', 'country'],
                ['address', 'present', 'state'],
                ['address', 'present', 'city'],
                ['address', 'present', 'zipCode'],

                ['address', 'permanent', 'line1'],
                ['address', 'permanent', 'line2'],
                ['address', 'permanent', 'country'],
                ['address', 'permanent', 'state'],
                ['address', 'permanent', 'city'],
                ['address', 'permanent', 'zipCode'],
            ],
            content: <AddressView form={form}/>,
        },
        {
            title: 'Social Profiles',
            fields: [
                ['social', 'linkedInProfileLink'],
                ['social', 'gitHubLink'],
                ['social', 'others']
            ],
            content: <SocialProfileView/>
        },
    ];

    // Reset fields of current step
    function handleResetCurrentStep() {
        const currentFields = steps[current].fields;
        form.resetFields(currentFields);
        message.info(`Step ${current + 1} fields reset`);
    }

    async function handleFormFinish() {
        try {
            const data: IContactDetailFormModel = await form.validateFields();
            console.log('Contact Details Form Values:', form.getFieldsValue());
            await props.onSubmit(data);
        } catch (errorInfo: any) {
            const firstError = errorInfo.errorFields?.[0];
            if (firstError) {
                const firstErrorPath = firstError.name[0];

                // find which step contains this error
                const errorStepIndex = steps.findIndex((step) =>
                    step.fields.some((field) => field[0] === firstErrorPath)
                );

                if (errorStepIndex >= 0) {
                    setCurrent(errorStepIndex);
                }

                // scroll to the field smoothly
                setTimeout(() => {
                    form.scrollToField(firstError.name, {block: 'center'});
                }, 200);
            }
        }
    }

    return (
        <div className={"pn__employee-create__tab contact-detail"}>
            <Steps
                current={current}
                onChange={setCurrent}
                style={{maxWidth: '1024px', margin: '16px auto 24px'}}
                className={"pn__employee-create__steps"}
            >
                {steps.map((step, i) => (
                    <Step
                        key={i}
                        title={step.title}
                        className={"pn__employee-create__step"}
                    />
                ))}
            </Steps>

            <Form
                form={form}
                layout={'vertical'}
                className={"pn__employee-create__form contact-detail-form"}
                initialValues={props.viewModel}
            >
                {
                    steps.map((step: IContactSteps, i: number) => (
                        <div
                            key={i}
                            className={"pn__employee-create__step-section"}
                            style={{display: i === current ? 'block' : 'none'}}
                        >
                            {step.content}
                        </div>
                    ))
                }
            </Form>

            {/* Floating Buttons */}
            <div className={'pn__employee-create__form-actions'}>
                <Button
                    danger
                    size={'large'}
                    disabled={props.loading}
                    onClick={handleResetCurrentStep}
                    className={'reset-button'}
                >
                    Reset Current Step
                </Button>
                <Button
                    type="primary"
                    size={'large'}
                    disabled={props.loading}
                    onClick={handleFormFinish}
                    className={'submit-button'}
                >
                    Submit Form
                </Button>
            </div>
        </div>
    );
};

export default ContactDetailView;
